"use client";

import {
  FormEvent,
  useState,
} from "react";

import { useRouter } from "next/navigation";

import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  LockKeyhole,
  Mail,
  User,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";

import { apiClient } from "@/lib/api";

type AuthStep =
  | "email"
  | "checking"
  | "login"
  | "register"
  | "sent";

type LoginResponse = {
  success: boolean;
  user_id: string;
};

function AuthPage() {
  const router = useRouter();

  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [step, setStep] =
    useState<AuthStep>("email");

  const [error, setError] =
    useState<string | null>(null);

  const [loggingIn, setLoggingIn] =
    useState(false);

  const [sendingSignup, setSendingSignup] =
    useState(false);

  function isTempleEmail(
    value: string
  ) {
    return value
      .trim()
      .toLowerCase()
      .endsWith("@temple.edu");
  }

  async function checkEmailExists(
    email: string
  ): Promise<boolean> {
    const { data } =
      await apiClient.post<{
        exists: boolean;
      }>(
        "/api/auth/user-exists",
        {
          email,
        }
      );

    return data.exists;
  }

  async function handleEmailSubmit(
    event: FormEvent
  ) {
    event.preventDefault();

    const normalizedEmail =
      email
        .trim()
        .toLowerCase();

    setError(null);

    if (!normalizedEmail) {
      setError(
        "Enter your Temple email address."
      );

      return;
    }

    if (
      !isTempleEmail(
        normalizedEmail
      )
    ) {
      setError(
        "Please use your @temple.edu email."
      );

      return;
    }

    setEmail(
      normalizedEmail
    );

    setStep(
      "checking"
    );

    try {
      const exists =
        await checkEmailExists(
          normalizedEmail
        );

      if (exists) {
        setStep(
          "login"
        );
      } else {
        setStep(
          "register"
        );
      }
    } catch {
      setError(
        "We couldn't check that email. Try again."
      );

      setStep(
        "email"
      );
    }
  }

  async function handleLogin(
    event: FormEvent
  ) {
    event.preventDefault();

    setError(null);

    if (!password) {
      setError(
        "Enter your password."
      );

      return;
    }

    setLoggingIn(true);

    try {
      const { data } =
        await apiClient.post<LoginResponse>(
          "/api/auth/login",
          {
            email,
            password,
          }
        );

      if (!data.success) {
        throw new Error(
          "Login failed."
        );
      }

      /*
       * The backend has now created the session
       * and sent the HttpOnly cookie.
       */
      setPassword("");

      router.replace("/");
      router.refresh();
    } catch (error: any) {
      const message =
        error?.response?.data?.error;

      if (
        typeof message === "string"
      ) {
        setError(
          message
        );
      } else {
        setError(
          "We couldn't sign you in. Check your password and try again."
        );
      }
    } finally {
      setLoggingIn(false);
    }
  }

  async function handleSendSignupEmail() {
    setError(null);

    setSendingSignup(true);

    try {
      const response =
        await apiClient.post(
          "/api/auth/start-signup",
          {
            email,
          }
        );

      if (
        response.status === 200
      ) {
        setStep(
          "sent"
        );
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.error;

      setError(
        typeof message === "string"
          ? message
          : "Failed to send the sign-up email. Please try again."
      );
    } finally {
      setSendingSignup(false);
    }
  }

  function resetEmail() {
    setPassword("");
    setError(null);

    setStep(
      "email"
    );
  }

  return (
    <main
      className="
        flex flex-1
        items-start justify-center
        px-4 py-12
        sm:px-6
        md:items-center
        md:py-16
      "
    >
      <div className="w-full max-w-md">
        {/* Heading */}

        <div className="mb-7 text-center">
          <div
            className="
              mx-auto mb-4
              flex h-12 w-12
              items-center justify-center
              rounded-2xl
              bg-[#9D2235]/10
              text-[#9D2235]
            "
          >
            {step === "login" ? (
              <LockKeyhole className="h-5 w-5" />
            ) : step === "register" ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <Mail className="h-5 w-5" />
            )}
          </div>

          <h1
            className="
              text-3xl font-bold
              tracking-tight
            "
          >
            {step === "login"
              ? "Welcome back"
              : step === "register"
                ? "Almost there..."
                : step === "sent"
                  ? "Check your inbox"
                  : "Continue with Temple"}
          </h1>

          <p
            className="
              mx-auto mt-2
              max-w-sm
              text-sm
              text-muted-foreground
            "
          >
            {step === "login"
              ? "Enter your password to continue."
              : step === "register"
                ? "We'll use your Temple email to create your account."
                : step === "sent"
                  ? "We sent you a link to continue creating your account."
                  : "Use your Temple email to sign in or create an account."}
          </p>
        </div>

        <Card
          className="
            overflow-hidden
            rounded-[28px]
            border
            shadow-sm
          "
        >
          <CardHeader className="pb-3">
            {step !== "email" &&
              step !== "checking" && (
                <button
                  type="button"
                  onClick={resetEmail}
                  disabled={
                    loggingIn ||
                    sendingSignup
                  }
                  className="
                    flex w-fit
                    items-center gap-1.5
                    rounded-full
                    px-2 py-1
                    text-xs
                    text-muted-foreground
                    transition-colors
                    hover:bg-muted
                    hover:text-foreground
                    disabled:pointer-events-none
                    disabled:opacity-50
                  "
                >
                  <ArrowLeft className="h-3.5 w-3.5" />

                  Change email
                </button>
              )}
          </CardHeader>

          <CardContent className="pb-6">
            {/* Email */}

            {step === "email" && (
              <form
                onSubmit={
                  handleEmailSubmit
                }
                className="space-y-5"
              >
                <div>
                  <label
                    htmlFor="email"
                    className="
                      mb-2 block
                      text-sm font-medium
                    "
                  >
                    Temple email
                  </label>

                  <div
                    className="
                      flex h-13
                      items-center
                      overflow-hidden
                      rounded-2xl
                      border
                      bg-background
                      transition-all
                      focus-within:border-primary
                      focus-within:ring-[3px]
                      focus-within:ring-primary/15
                    "
                  >
                    <Mail
                      className="
                        ml-4
                        size-4
                        shrink-0
                        text-muted-foreground
                      "
                    />

                    <Input
                      id="email"
                      type="text"
                      value={
                        email.replace(
                          /@temple\.edu$/i,
                          ""
                        )
                      }
                      onChange={(
                        event
                      ) => {
                        const username =
                          event.target.value
                            .replace(
                              /@temple\.edu$/i,
                              ""
                            )
                            .replace(
                              /@.*$/,
                              ""
                            )
                            .replace(
                              /\s/g,
                              ""
                            );

                        setEmail(
                          username
                            ? `${username}@temple.edu`
                            : ""
                        );
                      }}
                      placeholder="tu123456"
                      autoComplete="email"
                      autoFocus
                      className="
                        h-full
                        min-w-0 flex-1
                        border-0
                        bg-transparent
                        px-3
                        text-base
                        shadow-none
                        focus-visible:ring-0
                        focus-visible:ring-offset-0
                      "
                    />

                    <div
                      className="
                        flex h-full
                        shrink-0
                        select-none
                        items-center
                        border-l
                        bg-muted/60
                        px-4
                        text-sm
                        font-medium
                        text-muted-foreground
                      "
                    >
                      @temple.edu
                    </div>
                  </div>

                  <p
                    className="
                      mt-2 text-xs
                      text-muted-foreground
                    "
                  >
                    You must use an @temple.edu
                    email address!
                  </p>

                  {error && (
                    <p
                      className="
                        mt-2
                        text-sm
                        text-destructive
                      "
                    >
                      {error}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="
                    h-12 w-full
                    rounded-full
                    text-base
                  "
                >
                  Continue

                  <ArrowRight
                    data-icon="inline-end"
                    className="h-4 w-4"
                  />
                </Button>
              </form>
            )}

            {/* Checking */}

            {step === "checking" && (
              <div
                className="
                  flex min-h-40
                  flex-col
                  items-center
                  justify-center
                  text-center
                "
              >
                <Loader2
                  className="
                    h-6 w-6
                    animate-spin
                    text-[#9D2235]
                  "
                />

                <p className="mt-4 font-medium">
                  Checking your email
                </p>

                <p
                  className="
                    mt-1 text-sm
                    text-muted-foreground
                  "
                >
                  {email}
                </p>
              </div>
            )}

            {/* Login */}

            {step === "login" && (
              <form
                onSubmit={
                  handleLogin
                }
                className="space-y-5"
              >
                <div
                  className="
                    rounded-2xl
                    bg-muted/60
                    px-4 py-3
                  "
                >
                  <p
                    className="
                      text-xs
                      text-muted-foreground
                    "
                  >
                    Signing in as
                  </p>

                  <p
                    className="
                      mt-0.5 truncate
                      text-sm font-medium
                    "
                  >
                    {email}
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="
                      mb-2 block
                      text-sm font-medium
                    "
                  >
                    Password
                  </label>

                  <div className="relative">
                    <LockKeyhole
                      className="
                        absolute left-4
                        top-1/2
                        h-4 w-4
                        -translate-y-1/2
                        text-muted-foreground
                      "
                    />

                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(
                        event
                      ) =>
                        setPassword(
                          event.target.value
                        )
                      }
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      autoFocus
                      disabled={loggingIn}
                      className="
                        h-13
                        rounded-2xl
                        pl-11
                        text-base
                      "
                    />
                  </div>

                  {error && (
                    <p
                      className="
                        mt-2
                        text-sm
                        text-destructive
                      "
                    >
                      {error}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loggingIn}
                  className="
                    h-12 w-full
                    rounded-full
                    text-base
                  "
                >
                  {loggingIn ? (
                    <>
                      <Loader2
                        data-icon="inline-start"
                        className="
                          h-4 w-4
                          animate-spin
                        "
                      />

                      Signing in...
                    </>
                  ) : (
                    <>
                      Log in

                      <ArrowRight
                        data-icon="inline-end"
                        className="h-4 w-4"
                      />
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  className="
                    mx-auto block
                    text-xs
                    text-muted-foreground
                    transition-colors
                    hover:text-foreground
                  "
                >
                  Forgot your password?
                </button>
              </form>
            )}

            {/* Register */}

            {step === "register" && (
              <div
                className="
                  flex flex-col
                  space-y-5
                "
              >
                <div
                  className="
                    flex flex-row
                    items-center
                    justify-start
                    gap-2
                    rounded-full
                    p-2
                    text-lg
                    text-[#9D2235]
                  "
                >
                  <div
                    className="
                      rounded-full
                      bg-[#9D2235]/10
                      p-2
                    "
                  >
                    <User className="h-5 w-5" />
                  </div>

                  <h2
                    className="
                      text-2xl
                      font-semibold
                    "
                  >
                    Create an account
                  </h2>
                </div>

                <div>
                  <p
                    className="
                      mt-2
                      text-sm
                      leading-relaxed
                      text-muted-foreground
                    "
                  >
                    We couldn&apos;t find
                    an account for{" "}
                    <span
                      className="
                        font-medium
                        text-foreground
                      "
                    >
                      {email}
                    </span>
                    .
                  </p>

                  <p
                    className="
                      mt-3
                      text-sm
                      leading-relaxed
                      text-muted-foreground
                    "
                  >
                    We&apos;ll send a
                    verification link to
                    your Temple email.
                    Open it to finish
                    creating your account.
                  </p>
                </div>

                {error && (
                  <p
                    className="
                      text-sm
                      text-destructive
                    "
                  >
                    {error}
                  </p>
                )}

                <Button
                  type="button"
                  onClick={
                    handleSendSignupEmail
                  }
                  disabled={
                    sendingSignup
                  }
                  className="
                    h-12 w-full
                    rounded-full
                    text-base
                  "
                >
                  {sendingSignup ? (
                    <>
                      <Loader2
                        data-icon="inline-start"
                        className="
                          h-4 w-4
                          animate-spin
                        "
                      />

                      Sending...
                    </>
                  ) : (
                    <>
                      Send sign-up email

                      <ArrowRight
                        data-icon="inline-end"
                        className="h-4 w-4"
                      />
                    </>
                  )}
                </Button>

                <p
                  className="
                    text-center
                    text-xs
                    text-muted-foreground
                  "
                >
                  Check your Temple inbox
                  after continuing.
                </p>
              </div>
            )}

            {/* Signup email sent */}

            {step === "sent" && (
              <div
                className="
                  flex flex-col
                  items-center
                  text-center
                "
              >
                <div
                  className="
                    flex h-14 w-14
                    items-center justify-center
                    rounded-full
                    bg-[#9D2235]/10
                    text-[#9D2235]
                  "
                >
                  <Mail className="h-6 w-6" />
                </div>

                <h2
                  className="
                    mt-5
                    text-xl
                    font-semibold
                  "
                >
                  Email sent
                </h2>

                <p
                  className="
                    mt-2 max-w-sm
                    text-sm
                    leading-relaxed
                    text-muted-foreground
                  "
                >
                  We sent a sign-up link to{" "}
                  <span
                    className="
                      font-medium
                      text-foreground
                    "
                  >
                    {email}
                  </span>
                  .
                </p>

                <div
                  className="
                    mt-6 w-full
                    rounded-2xl
                    bg-muted/50
                    px-5 py-4
                    text-left
                  "
                >
                  <p className="text-sm font-medium">
                    What happens next?
                  </p>

                  <ol
                    className="
                      mt-3
                      space-y-3
                      text-sm
                      text-muted-foreground
                    "
                  >
                    <li className="flex gap-3">
                      <span
                        className="
                          flex h-6 w-6
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          bg-background
                          text-xs
                          font-medium
                          text-foreground
                          shadow-sm
                        "
                      >
                        1
                      </span>

                      <span>
                        Check your Temple
                        email inbox.
                      </span>
                    </li>

                    <li className="flex gap-3">
                      <span
                        className="
                          flex h-6 w-6
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          bg-background
                          text-xs
                          font-medium
                          text-foreground
                          shadow-sm
                        "
                      >
                        2
                      </span>

                      <span>
                        Open the sign-up email
                        and click the verification
                        link.
                      </span>
                    </li>

                    <li className="flex gap-3">
                      <span
                        className="
                          flex h-6 w-6
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          bg-background
                          text-xs
                          font-medium
                          text-foreground
                          shadow-sm
                        "
                      >
                        3
                      </span>

                      <span>
                        Finish setting up your
                        account from there.
                      </span>
                    </li>
                  </ol>
                </div>

                <p
                  className="
                    mt-5 max-w-sm
                    text-xs
                    leading-relaxed
                    text-muted-foreground
                  "
                >
                  You can safely close this tab.
                  Your sign-up will continue from
                  the link in your email.
                </p>

                {error && (
                  <p
                    className="
                      mt-3
                      text-sm
                      text-destructive
                    "
                  >
                    {error}
                  </p>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="
                    mt-3 rounded-full
                    text-muted-foreground
                  "
                  onClick={
                    handleSendSignupEmail
                  }
                  disabled={
                    sendingSignup
                  }
                >
                  {sendingSignup
                    ? "Sending..."
                    : "Resend email"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p
          className="
            mt-5 text-center
            text-xs
            text-muted-foreground
          "
        >
          Clubs @ Temple
        </p>
      </div>
    </main>
  );
}

export default AuthPage;